import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type CrewDefinitionManagerSlotFormSchema,
	crewDefinitionManagerSlotSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { CrewDefinitionManagerSlotForm } from "@/forms/crew-definition-manager-slot-form";
import { client } from "@/hono";

/** Props Interface */
interface EditManagerSlotModalProps extends ModalProps {
	crewDefinitionId: string;
	managerSlotId: string;
	crewStartDate: string;
	crewEndDate: string | null;
	defaultValues: CrewDefinitionManagerSlotFormSchema;
}

/**
 * Move a manager's window, or hand it to somebody else. The span stays bounded by the crew's own
 * dates, so an edit cannot widen a window past the crew it is on.
 */
export function EditManagerSlotModal({
	isOpen,
	onClose,
	crewDefinitionId,
	managerSlotId,
	crewStartDate,
	crewEndDate,
	defaultValues,
}: EditManagerSlotModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm<CrewDefinitionManagerSlotFormSchema>({
		resolver: resolver(crewDefinitionManagerSlotSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: CrewDefinitionManagerSlotFormSchema) {
		const response = await client.crew_definitions[":crewDefinitionId"]["manager-slots"][
			":managerSlotId"
		].$patch({ param: { crewDefinitionId, managerSlotId }, json });
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["crew-definitions"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
				title={t("planning.crews.edit_manager_slot")}
				width="500px"
				data-cy="edit-manager-slot-modal"
			>
				<Modal.Body>
					<CrewDefinitionManagerSlotForm
						crewStartDate={crewStartDate}
						crewEndDate={crewEndDate}
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
